
import { useState, useEffect } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { ErrorBoundary } from '@/components/ui/error-boundary';
import { motion } from 'framer-motion';
import { fadeInVariants } from '@/lib/animations';
import { CircleDecor } from '@/components/ui/visual-elements';
import ComplianceHeader from '@/components/corporate/compliance/ComplianceHeader';
import ComplianceTabs from '@/components/corporate/compliance/ComplianceTabs';
import NRComplianceInfo from '@/components/corporate/compliance/NRComplianceInfo';
import { toast } from "@/hooks/use-toast";
import { useAnimationSimplifier } from '@/hooks/use-animation-simplifier';

export default function MentalHealthCompliance() {
  console.log("Rendering MentalHealthCompliance page"); // Debug log
  
  // Company data would typically come from an API
  const companyData = {
    id: '12345',
    name: 'Tech Solutions Inc.',
  };
  
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const { screenSize } = useAnimationSimplifier();
  
  useEffect(() => {
    // Simulate data loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);
  
  const handleAssessmentComplete = (results: any) => {
    try {
      console.log('Assessment completed:', results);
      // In a real implementation, this would save results to a database
      // and potentially update the compliance dashboard
      
      toast({
        title: "Avaliação concluída",
        description: "Os dados da sua avaliação de conformidade foram salvos.",
        variant: "default",
      });
    } catch (error) {
      console.error("Error saving assessment results:", error);
      setHasError(true);
      
      toast({
        title: "Erro ao salvar dados",
        description: "Houve um problema ao salvar os dados da sua avaliação.",
        variant: "destructive",
      });
    }
  };
  
  const handleError = (error: Error, errorInfo: React.ErrorInfo, componentName?: string) => {
    console.error(`Error in ${componentName || 'MentalHealthCompliance'}:`, error);
    console.error("Component stack:", errorInfo.componentStack);
    setHasError(true);
  };

  // Adjust the Circle decoration visibility and sizing based on screen size
  const showLargeDecoration = screenSize !== 'xs' && screenSize !== 'sm';
  const showSmallDecoration = screenSize !== 'xs';

  return (
    <ErrorBoundary
      fallback={
        <MainLayout>
          <div className="min-h-screen p-3 sm:p-6">
            <div className="bg-red-50 border border-red-200 rounded-md p-4 sm:p-6 text-center">
              <h2 className="text-base sm:text-lg font-medium text-red-800 mb-2">Erro ao carregar Dashboard de Compliance</h2>
              <p className="text-sm sm:text-base text-red-600 mb-4">Estamos enfrentando dificuldades técnicas com esta página.</p>
              <button 
                onClick={() => window.location.reload()} 
                className="px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
              >
                Recarregar Página
              </button>
            </div>
          </div>
        </MainLayout>
      }
      onError={handleError}
      componentName="MentalHealthCompliance"
    >
      <MainLayout>
        <motion.div
          initial="initial"
          animate="animate"
          variants={fadeInVariants}
          className="min-h-screen relative"
        >
          {showLargeDecoration && (
            <CircleDecor className="absolute top-10 right-10 opacity-10" variant="secondary" size="lg" />
          )}
          {showSmallDecoration && (
            <CircleDecor className="absolute bottom-20 left-10 opacity-10" size="sm" />
          )}
          
          <div className="w-full py-4 sm:py-6 px-3 sm:px-4 md:container md:mx-auto">
            <ComplianceHeader 
              title="Vive Calme | Saúde Mental Corporativa" 
              description="Plataforma para gestão integrada de saúde mental e conformidade com a NR-1" 
            />
            
            {isLoading ? (
              <div className="flex justify-center py-8 sm:py-12">
                <div className="animate-spin h-6 w-6 sm:h-8 sm:w-8 border-4 border-blue-500 rounded-full border-t-transparent"></div>
              </div>
            ) : hasError ? (
              <div className="p-3 sm:p-4 bg-red-50 border border-red-300 rounded-md text-red-700">
                <h3 className="font-medium text-base sm:text-lg">Erro ao carregar dados</h3>
                <p className="text-xs sm:text-sm mt-2">Ocorreu um problema ao carregar os componentes desta página.</p>
                <button 
                  onClick={() => window.location.reload()} 
                  className="mt-2 sm:mt-3 px-2 py-1 sm:px-3 sm:py-1.5 text-xs sm:text-sm bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Tentar novamente
                </button>
              </div>
            ) : (
              <ErrorBoundary 
                fallback={
                  <div className="p-3 sm:p-4 bg-red-50 border border-red-300 rounded-md text-red-700">
                    <h3 className="font-medium text-sm sm:text-base">Ocorreu um erro ao carregar os componentes desta página.</h3>
                    <button 
                      onClick={() => window.location.reload()}
                      className="mt-2 px-2 py-1 text-xs sm:text-sm bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Recarregar
                    </button>
                  </div>
                }
                componentName="ComplianceTabs"
              >
                <ComplianceTabs 
                  companyId={companyData.id}
                  companyName={companyData.name}
                  onAssessmentComplete={handleAssessmentComplete}
                />
              </ErrorBoundary>
            )}
            
            <ErrorBoundary
              fallback={<div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gray-100 border border-gray-200 rounded-md text-xs sm:text-sm">Informações de conformidade indisponíveis</div>}
              componentName="NRComplianceInfo"
            >
              <NRComplianceInfo />
            </ErrorBoundary>
          </div>
        </motion.div>
      </MainLayout>
    </ErrorBoundary>
  );
}
